import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle2, AlertCircle, Check, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "Contains a letter", test: (p) => /[A-Za-z]/.test(p) },
  { label: "Contains a number", test: (p) => /[0-9]/.test(p) },
  { label: "Contains a special character (!@#$%^&*)", test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(p) },
];

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const allRulesPassed = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");

    if (!allRulesPassed) {
      setError("Password does not meet all requirements");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="pt-20 min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="bg-white p-8 max-w-md w-full mx-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#0F172A] mb-2">Invalid Reset Link</h2>
          <p className="text-[#64748B] mb-6">This password reset link is invalid. Please request a new one.</p>
          <Button asChild className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none">
            <Link to="/portal">Go to Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-8 max-w-md w-full mx-4"
      >
        {success ? (
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">Password Reset Successful</h2>
            <p className="text-[#64748B] mb-6">Your password has been updated. You can now log in with your new password.</p>
            <Button asChild className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none w-full" data-testid="go-to-login">
              <Link to="/portal">Go to Login</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-[#F97316]" />
              </div>
              <h2 className="text-xl font-bold text-[#0F172A]">Reset Your Password</h2>
              <p className="text-sm text-[#64748B] mt-1">Create a strong password for your account</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-4 flex items-center gap-2" data-testid="reset-error">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#334155] block mb-1">New Password</label>
                <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  className="rounded-none pr-11"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#334155] transition-colors"
                  data-testid="toggle-new-password"
                >
                  {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
                </div>
                {/* Password strength rules */}
                {password.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {PASSWORD_RULES.map((rule) => {
                      const passed = rule.test(password);
                      return (
                        <div key={rule.label} className="flex items-center gap-1.5 text-xs">
                          {passed ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <X className="w-3.5 h-3.5 text-red-400" />
                          )}
                          <span className={passed ? "text-green-600" : "text-[#94A3B8]"}>{rule.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-[#334155] block mb-1">Confirm Password</label>
                <div className="relative">
                <Input
                  type={showConfirmPwd ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="rounded-none pr-11"
                  data-testid="confirm-password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#334155] transition-colors"
                  data-testid="toggle-confirm-password"
                >
                  {showConfirmPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
                </div>
                {confirmPassword.length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs mt-1.5">
                    {passwordsMatch ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-green-500" />
                        <span className="text-green-600">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <X className="w-3.5 h-3.5 text-red-400" />
                        <span className="text-red-400">Passwords do not match</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <Button
                type="submit"
                disabled={loading || !allRulesPassed || !passwordsMatch}
                className="w-full bg-[#F97316] hover:bg-orange-600 text-white rounded-none py-5 font-semibold disabled:opacity-50"
                data-testid="reset-submit"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>

            <p className="text-center text-sm text-[#64748B] mt-4">
              <Link to="/portal" className="text-[#F97316] hover:underline">Back to Login</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
